import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ZoneModel } from 'src/app/models/archive/zone.model';
import { ZoneService } from 'src/app/services/archive/zone.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-zone',
  templateUrl: './register-zone.component.html',
  styleUrls: ['./register-zone.component.scss'],
  standalone: false
})
export class RegisterZoneComponent implements OnInit {

  branchId: number = 0;
  disabledForm: boolean = false;
  formZone: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listSellers: any[] = [];
  nameWindow: string = 'Captura Zonas';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  severityState: string = 'success';
  stateTransporter: string = '';
  titleState: string = '';
  zoneContract: ZoneModel = new ZoneModel();
  zoneId: number = 0;
  url: string = 'zone';

  constructor(private activatedRoute: ActivatedRoute,
    private branchService: BranchService,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService,
    private zoneService: ZoneService) {
    this.validateForm(new ZoneModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.zoneId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar la zona. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.zoneId || this.zoneId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear una zona.'
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la zona?')) {
      try {
        await firstValueFrom(this.zoneService.deleteZone(this.zoneContract.zoneId));
        this.createNewRegister();
        this.toastHandlerService.showToast(
          'success',
          'La zona fue eliminada exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar la zona.', error);
      }
    }
  }

  async changeBranch(branchId: number) {
    this.zoneContract = new ZoneModel();
    const branchChanged = this.branchId != branchId;
    if (branchChanged) {
      this.branchId = branchId;
      this.zoneContract.branchId = branchId;
      await this.getAllLists(true);
    }
    this.validateForm(this.zoneContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async changeCode(code: number) {
    if (!code || this.zoneContract.code === code) { return; }
    const branchId = this.formZone.get('branchId')?.value;
    if (!branchId) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
      return;
    }
    try {
      const response = await firstValueFrom(this.zoneService.getZoneByCode(branchId, code));
      this.zoneId = response.zoneId;
      this.zoneContract = response;
      this.validateForm(this.zoneContract);
      this.location.replaceState(`/${this.url}/register/${this.zoneId}`);
    } catch (error) {
      console.error('Error al buscar la zona por código.', error);
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear zonas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.zoneId = 0;
    this.zoneContract = new ZoneModel();
    this.zoneContract.branchId = this.branchId;
    this.validateForm(this.zoneContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async getAllLists(reload: boolean) {
    try {
      if (reload) {
        this.listSellers = await firstValueFrom(this.zoneService.getAllSellersForSelect(this.branchId));
      } else {
        const [branches, sellers] = await Promise.all([
          firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
          firstValueFrom(this.zoneService.getAllSellersForSelect(this.branchId)),
        ]);
        this.listBranches = branches;
        this.listSellers = sellers;
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    const branchId = this.formZone.get("branchId")?.value;
    const code = this.formZone.get("code")?.value;
    if (branchId == null || branchId == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
      return;
    }
    this.handleZoneNavigation(buttonClick, branchId, code)
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const response = await firstValueFrom(
        this.userAuthenticatedService.branchObservable.pipe(
          filter(branch => !!branch && branch.branchId !== 0)
        )
      );
      this.branchId = response.branchId;
    } catch (error) {
      console.error('Error al obtener la primera sucursal del usuario autenticado.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getFirstBranchByUserAuthenticatedAsync();
    await Promise.all([
      this.getPermissionProfileByUrl(),
      this.getAllLists(false),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getZoneById() {
    try {
      const response = await firstValueFrom(this.zoneService.getZoneById(this.zoneId));
      this.zoneContract = response;
      this.validateForm(response);
    } catch (error) {
      console.error('Error al obtener la zona por Id.', error);
    }
  }

  async handleInitialState() {
    if (this.zoneId !== null && this.zoneId !== 0) {
      await this.getZoneById();
    } else {
      this.createNewRegister();
    }
  }

  async handleZoneNavigation(buttonClick: any, branchId: number, code: number) {
    const actionMap: Record<string, () => Promise<ZoneModel>> = {
      first: () =>
        firstValueFrom(this.zoneService.getFirstZoneByKeysAsync(branchId)),
      previus: () =>
        firstValueFrom(this.zoneService.getPreviusZoneByKeysAsync(branchId, code)),
      next: () =>
        firstValueFrom(this.zoneService.getNextZoneByKeysAsync(branchId, code)),
      last: () =>
        firstValueFrom(this.zoneService.getLastZoneByKeysAsync(branchId)),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.zoneId = 0;
      this.zoneContract = new ZoneModel();
      this.zoneContract.branchId = branchId;
      return;
    }
    try {
      const zone = await action();
      this.zoneId = zone.zoneId;
      this.zoneContract = zone;
      this.validateForm(this.zoneContract);
      this.location.replaceState(`/${this.url}/register/${this.zoneId}`);
    } catch (error) {
      this.zoneContract = new ZoneModel();
      this.zoneContract.zoneId = 0;
      this.zoneContract.branchId = branchId;
      this.zoneContract.code = code;
      console.error(`Error al buscar la zona (${buttonClick.target.id}).`, error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formZone.valid) {
      const modelZone = this.formZone.getRawValue() as ZoneModel;
      if (this.zoneId == 0) {
        try {
          const response = await firstValueFrom(this.zoneService.createZone(modelZone));
          this.zoneId = response.zoneId;
          this.zoneContract = response;
          this.validateForm(this.zoneContract);
          this.location.replaceState(`/${this.url}/register/${this.zoneId}`);
          this.toastHandlerService.showToast(
            'success',
            `La zona fue creada exitosamente.`
          );
        } catch (error) {
          console.error('Error al crear la zona.', error);
        }
      } else {
        this.zoneContract.branchId = modelZone.branchId;
        this.zoneContract.code = modelZone.code;
        this.zoneContract.name = modelZone.name;
        this.zoneContract.sellerId = modelZone.sellerId;
        try {
          const response = await firstValueFrom(this.zoneService.updateZone(this.zoneContract));
          this.zoneContract = response;
          this.validateForm(this.zoneContract);
          this.toastHandlerService.showToast(
            'success',
            `La zona fue modificada exitosamente.`
          );
        } catch (error) {
          console.error('Error al actualizar la zona.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.'
      },
      code: {
        required: 'El campo Código es obligatorio.'
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede tener más de 50 caracteres.'
      },
      sellerId: {
        required: 'El campo Vendedor es obligatorio.',
        min: 'Debe seleccionar un Vendedor válido.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formZone.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    const controlCode = this.formZone.controls['code'];
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formZone.enable());
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formZone.disable());
    }
    setTimeout(() => this.zoneContract.zoneId != 0 ?
      controlCode.enable() : controlCode.disable());
  }

  validateForm(zone: ZoneModel) {
    this.formZone = this.formBuilder.group({
      branchId: [zone.branchId, [Validators.required]],
      code: [zone.code, [Validators.required]],
      name: [zone.name, [Validators.required, Validators.maxLength(50)]],
      sellerId: [zone.sellerId, [Validators.required, Validators.min(1)]]
    });
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formZone.controls).forEach((key) => {
      const control = this.formZone.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
